import { useState, useEffect, useMemo } from 'react';
import { UserContextState, FocusCard, Account, Transaction, HistoryItem, UserFocusItem, ReflectionType, FocusInteractionOptions } from '@shared/types';
import { MOCK_HISTORY } from '../../services/mockData';
import { generateDailyCards } from '@shared/services/cardEngine';
import { mapFocusItemsToCards } from '@shared/services/userContentService';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';


interface UseAppStateParams {
    focusItems: UserFocusItem[];
    focusHasDocs: boolean;
    focusLoading: boolean;
    contentRefreshing: boolean;
    contentSettled: boolean;
    markFocusCardComplete: (id: string, responses?: Record<string, string | number | string[]>, options?: FocusInteractionOptions) => void;
    updateTransactionReflection: (id: string, reflection: any) => Promise<boolean>;
    connectBank: () => Promise<void>;
}

interface UseAppStateReturn {
    contextState: UserContextState;
    accounts: Account[];
    wellnessScore: number;
    setWellnessScore: React.Dispatch<React.SetStateAction<number>>;
    mindfulMinutes: number;
    setMindfulMinutes: React.Dispatch<React.SetStateAction<number>>;
    focusCards: FocusCard[];
    setFocusCards: React.Dispatch<React.SetStateAction<FocusCard[]>>;
    transactions: Transaction[];
    setPendingReflections: React.Dispatch<React.SetStateAction<Map<string, ReflectionType>>>;
}

export const useAppState = ({
    focusItems,
    focusHasDocs,
    focusLoading,
    contentRefreshing,
    contentSettled,
    markFocusCardComplete,
    updateTransactionReflection,
    connectBank
}: UseAppStateParams): UseAppStateReturn => {
    const { currentUser } = useAuth();

    const { plaidAssets, unifiedLiabilities, transactions: realTransactions, loadingData } = useData();

    // Merge all accounts for use in other views (focus cards, payments, etc.)
    const [accounts, setAccounts] = useState<Account[]>([]);

    // Pending reflections overlay — survives Firestore sync overwrites
    const [pendingReflections, setPendingReflections] = useState<Map<string, ReflectionType>>(new Map());

    // Merge DataContext transactions with pending optimistic reflections
    const transactions = useMemo(() => {
        if (pendingReflections.size === 0) return realTransactions;
        return realTransactions.map(tx => {
            const pending = pendingReflections.get(tx.id);
            return pending !== undefined ? { ...tx, reflection: pending, reflectionAppliedByRule: false } : tx;
        });
    }, [realTransactions, pendingReflections]);

    // Clean up pending reflections once Firestore catches up
    useEffect(() => {
        if (pendingReflections.size === 0) return;
        setPendingReflections(prev => {
            const next = new Map(prev);
            let changed = false;
            for (const [id, reflection] of prev) {
                const tx = realTransactions.find(t => t.id === id);
                if (tx?.reflection === reflection) {
                    next.delete(id);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [realTransactions, pendingReflections]);

    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [focusCards, setFocusCards] = useState<FocusCard[]>([]); // Start empty, generated dynamically
    const [wellnessScore, setWellnessScore] = useState(42); // Starting low to show growth
    const [mindfulMinutes, setMindfulMinutes] = useState(12); // Mock starting value
    const [isLoading, setIsLoading] = useState(true);

    // Sync accounts from Firestore (transactions are handled via useMemo + pending overlay above)
    useEffect(() => {
        if (!loadingData) {
            // Combine all accounts: Plaid assets + unified liabilities (from unifiedAccounts)
            const liabilityAccounts = unifiedLiabilities.map(u => ({
                id: u.id,
                name: u.name,
                balance: u.currentBalance ?? 0,
                type: (u.accountType === 'credit' ? 'credit' : 'loan') as 'credit' | 'loan',
                institution: u.institution ?? 'Unknown',
                limit: u.creditLimit,
                apr: u.apr,
                minPayment: u.minimumPayment,
                dueDate: u.nextDueDate
            }));
            setAccounts([...plaidAssets, ...liabilityAccounts]);

            setHistory(MOCK_HISTORY);
            // Focus cards are now generated via effect below once data is ready
            setIsLoading(false);
        }
    }, [loadingData, plaidAssets, unifiedLiabilities]);

    // Generate personalized cards when data changes
    useEffect(() => {
        if (!isLoading) {
            if (focusItems.length > 0) {
                setFocusCards(mapFocusItemsToCards(focusItems));
                return;
            }

            if (!focusLoading && !focusHasDocs && accounts.length > 0) {
                const cards = generateDailyCards(accounts, transactions, wellnessScore);
                setFocusCards(cards);
            }
        }
    }, [isLoading, accounts, transactions, wellnessScore, focusItems, focusHasDocs, focusLoading]);

    // Goal outcome processing is now handled by useGoalCelebrations (tied to day change)

    const contextState: UserContextState = {
        accounts,
        transactions,
        focusCards,
        history,
        wellnessScore,
        mindfulMinutes,
        isLoading,
        contentRefreshing,
        contentSettled,
        markFocusCardComplete,
        updateTransactionReflection,
        connectBank,
        focusItems, // Add focusItems to context for form rendering
        focusHasDocs,
    };

    return {
        contextState,
        accounts,
        wellnessScore,
        setWellnessScore,
        mindfulMinutes,
        setMindfulMinutes,
        focusCards,
        setFocusCards,
        transactions,
        setPendingReflections
    };
};
